import { Layout as AntLayout } from "antd";
import classNames from "classnames";
import React, { useCallback, useState } from "react";

import { BreadcrumbItem, User } from "../../types";

import Breadcrumbs from "./Breadcrumbs";
import Header from "./Header";
import Sidebar from "./Sidebar";

const { Content } = AntLayout;

interface LayoutProps {
  children: React.ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  user?: User;
}

const EMPTY_BREADCRUMBS: BreadcrumbItem[] = [];

const Layout: React.FC<LayoutProps> = ({
  children,
  breadcrumbs = EMPTY_BREADCRUMBS
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  // Stable handlers: the layout shell re-renders whenever a page updates its own state, and these
  // keep the memoized Sidebar/Header out of that re-render.
  const toggleSidebar = useCallback(() => setSidebarOpen((open) => !open), []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const toggleCollapsed = useCallback(
    () => setCollapsed((isCollapsed) => !isCollapsed),
    []
  );

  return (
    <AntLayout style={{ minHeight: "100vh" }}>
      <Sidebar
        isOpen={sidebarOpen}
        onClose={closeSidebar}
        collapsed={collapsed}
        toggleCollapsed={toggleCollapsed}
      />

      {/* Reserve the sidebar's width on Tailwind's `lg` (1024px) — the same breakpoint Sidebar and
          Header use to show/hide it. antd's `lg` is 992px, so keying this off Grid.useBreakpoint()
          left a blank 220px gutter between 992–1023px where the sidebar was already hidden. */}
      <AntLayout
        className={classNames(
          "transition-[padding] duration-300",
          collapsed ? "lg:pl-[80px]" : "lg:pl-[220px]"
        )}
      >
        <Header onMenuToggle={toggleSidebar} />

        <Content
          style={{
            padding: "var(--space-8)",
            backgroundColor: "var(--color-background-secondary)"
          }}
        >
          <Breadcrumbs items={breadcrumbs} />
          <div className="p-4">{children}</div>
        </Content>
      </AntLayout>
    </AntLayout>
  );
};

export default Layout;
